import Papa from "papaparse";

type CSVFieldType = "text" | "longtext" | "number" | "url" | "date" | "switch" | "singleselect";

export interface ParsedCSVRow {
  [key: string]: string;
}

export interface InferredField {
  name: string;
  displayName: string;
  fieldType: CSVFieldType;
  isRequired: boolean;
}

export interface CSVImportResult {
  fields: InferredField[];
  rows: ParsedCSVRow[];
  errors: Papa.ParseError[];
}

/**
 * Parse CSV file and infer field types
 */
export const parseCSV = (file: File): Promise<CSVImportResult> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { data, errors } = results;

        if (data.length === 0) {
          reject(new Error("CSV file is empty or has no valid data"));
          return;
        }

        // Convert data and infer field types
        const convertedData = convertDataTypes(data as ParsedCSVRow[]);
        const fields = inferFieldTypes(convertedData);

        resolve({
          fields,
          rows: convertedData,
          errors: errors || [],
        });
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

/**
 * Convert data types based on content
 */
const convertDataTypes = (rows: ParsedCSVRow[]): ParsedCSVRow[] => {
  if (rows.length === 0) return rows;

  const fieldNames = Object.keys(rows[0]);

  return rows.map((row) => {
    const convertedRow: ParsedCSVRow = {};

    fieldNames.forEach((fieldName) => {
      const value = row[fieldName];

      if (value === null || value === undefined || value === "") {
        convertedRow[fieldName] = value;
        return;
      }

      const stringValue = String(value).trim();

      // Convert boolean-like strings to actual booleans
      if (["true", "false", "yes", "no", "1", "0"].includes(stringValue.toLowerCase())) {
        convertedRow[fieldName] = ["true", "yes", "1"].includes(stringValue.toLowerCase());
        return;
      }

      // Convert numeric strings to numbers
      if (!Number.isNaN(Number(stringValue)) && stringValue !== "") {
        convertedRow[fieldName] = Number(stringValue);
        return;
      }

      // Keep as string for everything else
      convertedRow[fieldName] = stringValue;
    });

    return convertedRow;
  });
};

/**
 * Infer field types based on data content
 */
const inferFieldTypes = (rows: ParsedCSVRow[]): InferredField[] => {
  if (rows.length === 0) return [];

  const fieldNames = Object.keys(rows[0]);
  const fields: InferredField[] = [];

  fieldNames.forEach((name) => {
    const values = rows
      .map((row) => row[name])
      .filter((val) => val !== null && val !== undefined && val !== "");
    const displayName = name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, " $1");

    let fieldType: CSVFieldType = "text";
    let isRequired = false;

    if (values.length > 0) {
      // Check if all values are URLs
      const urlPattern = /^https?:\/\/.+/i;
      if (values.every((val) => typeof val === "string" && urlPattern.test(val))) {
        fieldType = "url";
      }
      // Check if all values are numbers
      else if (values.every((val) => typeof val === "number")) {
        fieldType = "number";
      }
      // Check if all values are booleans
      else if (values.every((val) => typeof val === "boolean")) {
        fieldType = "switch";
      }
      // Check if all values are dates
      else if (values.every((val) => val != null && !Number.isNaN(Date.parse(String(val))))) {
        fieldType = "date";
      }
      // Check if any value is long (more than 100 characters)
      else if (values.some((val) => typeof val === "string" && val.length > 100)) {
        fieldType = "longtext";
      }
      // Default to text
      else {
        fieldType = "text";
      }

      // Check if field has values in all rows (required)
      isRequired = values.length === rows.length;
    }

    fields.push({
      name,
      displayName,
      fieldType,
      isRequired,
    });
  });

  return fields;
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (data: Record<string, unknown>[], filename: string = "export.csv") => {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
